import { RemovableRef, useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { UnwrapRef } from 'vue';

interface State {
  settings: { [key: string]: RemovableRef<string> };
}

/**
 * This store is used to make global app settings available throughout the app.
 * These settings can be overridden by setting them in the local storage of the browser.
 *
 * In development mode, the values from the .env file are *always* used,
 * meaning the local storage overrides are reset when the page is reloaded.
 */
export const useAppSettingsStore = defineStore('app-settings', {
  state: () => {
    const settings: { [key: string]: RemovableRef<string> } = {};
    Object.entries(import.meta.env as [string, string]).forEach(([key, value]) => {
      if (!key.startsWith('VITE_')) return;

      settings[key] = useLocalStorage(key, value, { listenToStorageChanges: true });
      if (import.meta.env.MODE === 'development') {
        settings[key].value = value; // always use the value from the .env file when in development mode
      }
    });

    return { settings } as State;
  },

  actions: {
    get(key: string): string {
      return this.settings[key] as unknown as UnwrapRef<string>; // since we use refs inside refs, vue automatically unwraps these values, so we cast to UnwrapRef
    },

    getAsBool(key: string): boolean {
      return (this.settings[key] as unknown as UnwrapRef<string>) === 'true';
    }
  }
});
